import React, { useRef, useState, useEffect } from 'react';
import pluralize from 'pluralize';
import InfiniteScroll from 'react-infinite-scroll-component';
// Local
import { Loading } from '../shared-components';
import ResourceMapHeader from './ResourceMapHeader';
import ResourceListItem from './ResourceListItem';
export default function ResourceMapUI({ globalConfig, globalState, listConfig, updateFilter, currentList, selectedResources, dataByUid, onResetMap, onResetFilters, onUpdateModalResource, onSelectResource, nextPage, }) {
    const searchRef = useRef(null);
    const [searchFocused, setSearchFocused] = useState(false);
    const [isFiltered, setIsFiltered] = useState(false);
    useEffect(() => {
        var _a;
        setIsFiltered(JSON.stringify(globalState.currentFilter) !== JSON.stringify(globalConfig.defaultFilter));
        const searchTerm = ((_a = searchRef.current) === null || _a === void 0 ? void 0 : _a.value) || '';
        if (globalState.currentFilter.query !== searchTerm)
            clearSearchInput();
    }, [globalState.currentFilter]);
    const filterString = () => {
        const { agency, category, county, city, fundingStream, recipient, year, featured, query, } = globalState.currentFilter;
        const isCitySelected = Boolean(city && city !== 'all');
        const isCountySelected = Boolean(county && county !== 'all');
        if (!listConfig.showFilterString)
            return '';
        if (listConfig.defaultFilterString && (!county || county == 'all'))
            return listConfig.defaultFilterString;
        const resourceLabel = `${featured ? 'featured ' : ''}${globalConfig.resourceLabel}`;
        let filterString = `No ${pluralize(resourceLabel, 0, false)} published`;
        if (globalState.totalRows)
            filterString = `Showing <span>${pluralize(resourceLabel, globalState.totalRows, true)}</span>`;
        let categoryString = '';
        let categoryPrefix = 'tagged ';
        if (category) {
            categoryString = ` ${categoryPrefix} <span>"${category}"</span>`;
        }
        if (categoryString.length)
            filterString += categoryString;
        let where = '';
        if (county == 'statewide') {
            if (listConfig.mapLocationName)
                where = ` across ${listConfig.mapLocationName}</span>`;
        }
        else if (county == 'all') {
            if (listConfig.mapLocationName)
                where = ` for counties within ${listConfig.mapLocationName}</span>`;
        }
        else if (isCountySelected) {
            where = ` ${listConfig.countyFilterPreposition} <span>${county} ${pluralize(globalConfig.countySuffixLabel, 1)}</span>`;
        }
        else if (isCitySelected) {
            where = ` in <span>${city}${(city && city == 'all') ? 'cities' : ''}</span>`;
        }
        else {
            let allString = '';
            if (listConfig.mapLocationName) {
                allString = listConfig.mapLocationName;
            }
            else if (globalState.totalRows) {
                allString = `all ${pluralize(globalConfig.countySuffixLabel.toLowerCase(), 0)}`;
            }
            else {
                allString = `any ${pluralize(globalConfig.countySuffixLabel.toLowerCase(), 0)}`;
            }
            where = ` ${listConfig.countyFilterPreposition} <span>${allString}</span>`;
        }
        if (where.length)
            filterString += where;
        let agencyString = '';
        let agencyPrefix = listConfig.agencyPrefix || 'from the ';
        if (agency) {
            agencyString = ` ${agencyPrefix}<span>${agency}</span>`;
        }
        if (agencyString.length)
            filterString += agencyString;
        let fundingStreamString = '';
        let fundingStreamPrefix = listConfig.fundingStreamPrefix || 'and funded by ';
        if (fundingStream) {
            fundingStreamString = ` ${fundingStreamPrefix}<span>${fundingStream}</span>`;
        }
        if (fundingStreamString.length)
            filterString += fundingStreamString;
        let yearString = '';
        let yearPrefix = listConfig.yearPrefix || 'during';
        if (year) {
            yearString = ` ${yearPrefix} <span>${year}</span>`;
        }
        if (yearString.length)
            filterString += yearString;
        let queryString = '';
        let queryPrefix = 'matching search term ';
        if (query) {
            queryString = ` ${queryPrefix} <span>"${query}"</span>`;
        }
        if (queryString.length)
            filterString += queryString;
        let recipientString = '';
        let recipientPrefix = 'allocated to ';
        if (recipient) {
            recipientString = ` ${recipientPrefix} <span>${recipient}</span>`;
        }
        if (recipientString.length)
            filterString += recipientString;
        return filterString;
    };
    const handleResetMap = () => {
        onResetMap();
    };
    const handleResetFilters = () => {
        onResetFilters();
    };
    const handleFilterChange = (event) => {
        var _a, _b;
        event.preventDefault();
        const { name, value } = event.target;
        const column = (_b = (_a = name === null || name === void 0 ? void 0 : name.split('-')) === null || _a === void 0 ? void 0 : _a.slice(1)) === null || _b === void 0 ? void 0 : _b[0];
        if (!column)
            return;
        const newFilter = {
            [column]: value
        };
        updateFilter(newFilter);
    };
    const handleSubmitSearch = (event) => {
        var _a, _b, _c, _d;
        event.preventDefault();
        const searchTerm = ((_a = searchRef.current) === null || _a === void 0 ? void 0 : _a.value) || '';
        const searchCols = ((_d = (_c = (_b = searchRef.current) === null || _b === void 0 ? void 0 : _b.id) === null || _c === void 0 ? void 0 : _c.split('-')) === null || _d === void 0 ? void 0 : _d.slice(1)) || 'project_name';
        updateFilter({
            query: searchTerm,
            queryCol: searchCols,
        });
    };
    const onClearSearch = (event) => {
        event.preventDefault();
        clearSearchInput();
        updateFilter({ query: '' });
    };
    const clearSearchInput = () => {
        const searchInput = searchRef.current;
        if (searchInput)
            searchInput.value = '';
    };
    const onSearchFocus = () => {
        setSearchFocused(true);
    };
    const onSearchBlur = () => {
        setSearchFocused(false);
    };
    return (React.createElement("div", { className: "row no-gutters flex-column flex-xl-row justify-content-end h-100" },
        React.createElement("div", { className: "EventPage__mapUiActiveCol col" },
            React.createElement("div", { className: "EventPage__resetButtonContainer" },
                React.createElement("button", { className: "js-filter-reset Button Button--secondary Button--sm", onClick: handleResetMap }, "Reset Map"))),
        React.createElement("div", { className: "EventPage__mapUiContentCol col-8 col-xl-auto mw-100 h-100" },
            React.createElement("div", { id: "EventPage__infiniteScrollWindow", className: "EventPage__contentBox h-100" },
                React.createElement("div", { className: "EventPage__header" },
                    React.createElement(ResourceMapHeader, { globalConfig: globalConfig })),
                React.createElement("div", { className: "EventPage__listControls" }, listConfig.filters.map((row, index) => {
                    return (React.createElement("div", { key: `list-control-${index}`, className: "EventPage__listControlRow row" }, row.map(filter => {
                        var _a, _b, _c, _d;
                        const fieldName = `${((_a = filter.columns) === null || _a === void 0 ? void 0 : _a.reduce((className, column) => `${className}-${column}`)) || filter.column || 'project_name'}`;
                        return (React.createElement("div", { className: `EventPage__listControlCol col${filter.width ? '-' + parseInt(filter.width) : ''}`, key: `list-control-col-${filter.column}` }, {
                            'divider': React.createElement(React.Fragment, null,
                                React.createElement("hr", { className: "EventList__divider" })),
                            'select': React.createElement(React.Fragment, null,
                                React.createElement("label", { className: "EventPage__listControlLabel", htmlFor: `select-${fieldName}`, dangerouslySetInnerHTML: { __html: filter.label } }),
                                React.createElement("select", { className: 'InputSelect', disabled: Boolean(!((_c = (_b = listConfig.distinctColValues) === null || _b === void 0 ? void 0 : _b[filter.column]) === null || _c === void 0 ? void 0 : _c.length)), id: `select-${fieldName}`, name: `select-${fieldName}`, onChange: (event) => handleFilterChange(event), value: globalState.currentFilter[filter.column] },
                                    React.createElement(SelectFilterGrouping, { defaults: filter.default, options: (_d = listConfig.distinctColValues) === null || _d === void 0 ? void 0 : _d[filter.column] }))),
                            'search': React.createElement(React.Fragment, null,
                                React.createElement("form", { className: `EventList__searchForm`, onSubmit: handleSubmitSearch },
                                    React.createElement("div", { className: "EventList__searchFormLabel" }, Boolean(filter.label) ?
                                        (React.createElement("label", { className: "EventPage__listControlLabel", htmlFor: `search-${fieldName}`, dangerouslySetInnerHTML: { __html: filter.label } })) : (React.createElement("label", { className: "sr-only", htmlFor: `search-${fieldName}` }, "Search items"))),
                                    React.createElement("div", { className: "EventList__searchFormField" },
                                        React.createElement("input", { name: `search-${fieldName}`, id: `search-${fieldName}`, ref: searchRef, className: "w-100", type: "text", onFocus: onSearchFocus, onBlur: onSearchBlur, placeholder: "Search" }),
                                        globalState.currentFilter.query &&
                                            React.createElement("button", { type: "button", className: `EventList__searchClearButton${searchFocused ? ' focus' : ''}`, onClick: onClearSearch },
                                                React.createElement("span", null, "Clear"),
                                                React.createElement("span", { className: "sr-only" }, "search query")),
                                        React.createElement("button", { type: "submit", className: "Button Button--primary" },
                                            React.createElement("span", { className: "sr-only" }, "Submit search query"),
                                            React.createElement("span", { className: "fa fa-search" })))))
                        }[filter.type]));
                    })));
                })),
                React.createElement("div", { className: "EventPage__listContainer pt-3" },
                    React.createElement("div", { className: "EventPage__returnToTop row no-gutters justify-content-end" },
                        React.createElement("div", { className: "col-auto pt-3 px-3" },
                            React.createElement("button", { className: "js-return-to-top Button Button--primary Button--xs" },
                                React.createElement("span", { className: "fa fa-filter" }),
                                "\u00A0Filter"))),
                    React.createElement("div", { className: "EventList__filterString" },
                        React.createElement("span", { dangerouslySetInnerHTML: { __html: filterString() } }),
                        listConfig.showFilterReset && isFiltered &&
                            React.createElement("button", { className: "EventList__filterResetButton", onClick: handleResetFilters },
                                React.createElement("span", { className: "Button__label" }, "Clear Filters"))),
                    React.createElement("div", { id: "react-event-list", className: "EventPage__eventList" },
                        React.createElement(InfiniteScroll, { style: { overflowY: 'hidden' }, dataLength: (currentList === null || currentList === void 0 ? void 0 : currentList.length) || 0, next: nextPage, hasMore: ((currentList === null || currentList === void 0 ? void 0 : currentList.length) < globalState.totalRows), loader: React.createElement(Loading, null), 
                            // scrollThreshold="200px"
                            scrollableTarget: "EventPage__infiniteScrollWindow" }, Boolean(dataByUid && (currentList === null || currentList === void 0 ? void 0 : currentList.length)) && currentList.map((uid) => {
                            if (!dataByUid[uid]) {
                                return React.createElement(React.Fragment, null);
                            }
                            const itemData = dataByUid[uid];
                            const isSelected = Boolean(selectedResources === null || selectedResources === void 0 ? void 0 : selectedResources.includes(uid));
                            return (React.createElement(ResourceListItem, { key: uid, data: itemData, globalConfig: globalConfig, openModalItem: onUpdateModalResource, selectItem: onSelectResource, isSelected: isSelected }));
                        }))))))));
}
const SelectFilterGrouping = ({ defaults, options }) => {
    const groups = [[]];
    const groupMeta = {};
    let currentGroup = 0;
    if ((defaults === null || defaults === void 0 ? void 0 : defaults.length) && Array.isArray(defaults[0])) {
        defaults.forEach(([label, value]) => {
            if (value.toLowerCase() == 'group') {
                currentGroup++;
                groupMeta[currentGroup] = { label };
                if (!Array.isArray(groups[currentGroup]))
                    groups[currentGroup] = [];
            }
            else {
                if (!Array.isArray(groups[currentGroup]))
                    groups[currentGroup] = [];
                groups[currentGroup].push([label, value]);
            }
        });
    }
    else if (defaults === null || defaults === void 0 ? void 0 : defaults.length) {
        const [label, value] = defaults;
        groups[currentGroup].push([label, value]);
    }
    if (options === null || options === void 0 ? void 0 : options.length)
        options.forEach(label => {
            // Rows from DB have same label and value in <option> tags
            groups[currentGroup].push([label, label]);
        });
    return (React.createElement(React.Fragment, null, Boolean(defaults === null || defaults === void 0 ? void 0 : defaults.length) && (groups.map((group, index) => (React.createElement(React.Fragment, null, index ? (React.createElement("optgroup", { key: groupMeta[index].label, label: groupMeta[index].label }, group.map(([label, value]) => (React.createElement("option", { key: `${value}`, value: value, dangerouslySetInnerHTML: { __html: label } }))))) : (group.map(([label, value]) => (React.createElement("option", { key: value, value: value, dangerouslySetInnerHTML: { __html: label } }))))))))));
};
